import { Button, Checkbox, Stack, TextField, Typography } from '@mui/material'
import {
  Reference,
  SourceDocument,
  TwinInstruction,
  TwinInstructionNew,
  TwinInstructionType,
  TwinInstructionUpdate,
  TwinNoteSummary,
  TwinQuestion,
} from 'api/model'
import JelledModal from 'components/JelledModal'
import { useState } from 'react'
import { isFieldEmpty } from 'utils'
import { AddDocumentDialog, AddNoteDialog } from '../AddOrEditInstructionModal'
import { useActiveOrganization } from 'hooks/use-organization'
import {
  twinsDeleteInstruction,
  twinsNewInstruction,
  twinsQuestion,
  twinsUpdateInstruction,
} from 'api/endpoints/jelledFromFileSpecWithTransformer'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import { questionTypeInstructionsAtom } from 'state/instructions'
import { authenticatedUserAtom, toastAtom } from 'state'
import { LoadingButton } from '@mui/lab'
import { useFetchMyTwin } from 'hooks/use-twin'
import DeleteOutlined from '@mui/icons-material/DeleteOutlined'
import VerboseLogModal from 'components/Debug/VerboseLogModal'
import ReferencesList from 'components/References/ReferencesList'
import ArrowDropDownOutlinedIcon from '@mui/icons-material/ArrowDropDownOutlined'
import ArrowDropUpOutlinedIcon from '@mui/icons-material/ArrowDropUpOutlined'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import AttachDocumentSelector from 'components/Gmail/AttachDocumentSelector'
import { DocumentType } from 'components/Gmail/AttachDocumentSelector'
import AttachedDocumentsList from 'components/Gmail/AttachedDocumentsList'
import useCheckMobileScreen from 'hooks/use-check-mobile-screen'
import { COLORS } from 'app-constants'

// this modal is used to create a new suggested question or edit an existing one
const SuggestedQuestionModal = ({
  suggestedQuestion,
  onClose,
}: {
  suggestedQuestion?: TwinInstruction
  onClose: () => void
}) => {
  const [activeOrganization] = useActiveOrganization()
  const user = useRecoilValue(authenticatedUserAtom)
  const isMobile = useCheckMobileScreen()

  const { myTwin } = useFetchMyTwin(user?.id ?? '')
  const setQuestionTypeInstructions = useSetRecoilState(questionTypeInstructionsAtom)
  const setToast = useSetRecoilState(toastAtom)

  // state for the form
  const [query, setQuery] = useState<string>(suggestedQuestion?.query || '')
  const onQueryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(event.target.value)
  }
  const [instructions, setInstructions] = useState<string>(suggestedQuestion?.instructions || '')
  const onInstructionsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInstructions(event.target.value)
  }

  // state for debugging
  const [verboseOutput, setVerboseOutput] = useState<string | null>(null)
  const [isVerboseLogModalOpen, setIsVerboseLogModalOpen] = useState<boolean>(false)

  // state for add note dialog
  const [isAddNoteDialogOpen, setIsAddNoteDialogOpen] = useState<boolean>(false)
  const [notes, setNotes] = useState<TwinNoteSummary[]>(suggestedQuestion?.inclusions.notes || [])

  // state for add document dialog
  const [isAddDocumentDialogOpen, setIsAddDocumentDialogOpen] = useState<boolean>(false)
  const [sourceDocuments, setSourceDocuments] = useState<SourceDocument[]>(
    suggestedQuestion?.inclusions.documents || []
  )

  const onGenerateAnswerClick = async () => {
    if (!activeOrganization || !myTwin) {
      return
    }

    try {
      setIsGeneratingAnswer(true)

      const twinQuestion: TwinQuestion = {
        twin_id: myTwin?.id,
        question: query,
        instruction: {
          instruction: instructions,
          answer,
          note_ids: notes.map((note) => note.id),
          document_ids: sourceDocuments.map((document) => document.id),
        },
        timezone_offset: -new Date().getTimezoneOffset(),
      }

      const response = await twinsQuestion(activeOrganization, twinQuestion, {
        verbose: user?.is_system_admin,
      })

      // update local state with the generated answer
      setAnswer(response.data.answer)

      if (response.data.verbose_output) {
        setVerboseOutput(response.data.verbose_output)
      }

      setToast({
        title: 'Successfully generated answer',
        severity: 'success',
      })
    } catch (error) {
      setToast({
        title: 'Failed to generate answer',
        severity: 'error',
      })
    } finally {
      setIsGeneratingAnswer(false)
    }
  }
  const [isGeneratingAnswer, setIsGeneratingAnswer] = useState<boolean>(false)
  const [answer, setAnswer] = useState<string>(suggestedQuestion?.answer || '')
  const [answerStrictly, setAnswerStrictly] = useState<boolean>(suggestedQuestion?.answer_strictly || true)

  // state for references
  const [references, setReferences] = useState<Reference[]>(suggestedQuestion?.references || [])

  // state for submitting the form
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [submitCount, setSubmitCount] = useState<number>(0)

  const onUpdateInstruction = async () => {
    setSubmitCount(submitCount + 1)

    if (!activeOrganization || !suggestedQuestion || !query || !answer || !myTwin) {
      return
    }

    try {
      setIsSubmitting(true)

      const twinInstructionUpdate: TwinInstructionUpdate = {
        query,
        instructions,
        type: TwinInstructionType.question,
        answer,
        answer_strictly: answerStrictly,
        references: references.length > 0 ? references : undefined,
        inclusions: {
          document_ids: sourceDocuments.map((document) => document.id),
          note_ids: notes.map((note) => note.id),
          notes_all: false,
          documents_all: false,
          calendars_all: false,
          calendar_ids: [],
          gmail_all: false,
          gmail_query_ids: [],
        },
      }

      const response = await twinsUpdateInstruction(
        activeOrganization,
        myTwin.id,
        suggestedQuestion?.id,
        twinInstructionUpdate
      )

      // update recoil state with updated instruction
      setQuestionTypeInstructions((instructions) => {
        return instructions.map((instruction) => {
          if (instruction.id === suggestedQuestion?.id) {
            return response.data
          } else {
            return instruction
          }
        })
      })

      onClose()

      setToast({
        title: 'Successfully updated suggested question',
        severity: 'success',
      })
    } catch (error) {
      setToast({
        title: 'Failed to update suggested question',
        severity: 'error',
      })
    }
  }

  const onAddInstruction = async () => {
    setSubmitCount(submitCount + 1)

    if (!activeOrganization || !query || !answer || !myTwin) {
      return
    }

    try {
      setIsSubmitting(true)

      const twinInstructionNew: TwinInstructionNew = {
        query,
        instructions,
        type: TwinInstructionType.question,
        answer,
        answer_strictly: answerStrictly,
        references: references.length > 0 ? references : undefined,
        inclusions: {
          document_ids: sourceDocuments.map((document) => document.id),
          note_ids: notes.map((note) => note.id),
          notes_all: false,
          documents_all: false,
          calendars_all: false,
          calendar_ids: [],
          gmail_all: false,
          gmail_query_ids: [],
        },
      }

      const response = await twinsNewInstruction(activeOrganization, myTwin.id, twinInstructionNew)

      // update recoil state with new instruction
      setQuestionTypeInstructions((instructions) => {
        return instructions.concat([response.data])
      })

      setToast({
        title: 'Successfully added suggested question',
        severity: 'success',
      })

      onClose()
    } catch (error) {
      setToast({
        title: 'Failed to add suggested question',
        severity: 'error',
      })
    }
  }

  const onDeleteInstruction = async () => {
    if (!activeOrganization || !myTwin || !suggestedQuestion) {
      return
    }

    try {
      setIsSubmitting(true)
      await twinsDeleteInstruction(activeOrganization, myTwin?.id, suggestedQuestion?.id)

      // remove instruction from recoil
      setQuestionTypeInstructions((instructions) => {
        return instructions.filter((instruction) => instruction.id !== suggestedQuestion?.id)
      })

      setToast({
        title: 'Successfully deleted suggested question',
        severity: 'success',
      })

      onClose()
    } catch (error) {
      setToast({
        title: 'Failed to delete suggested question',
        severity: 'error',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  // used to keep track of whether or not the user has opened the generate answer section
  const [isGenerateAnswerSectionOpen, setIsGenerateAnswerSectionOpen] = useState<boolean>(false)
  const onClickToggleGenerateAnswerSection = () => {
    setIsGenerateAnswerSectionOpen(!isGenerateAnswerSectionOpen)
  }

  // used for the attach relevant documents selector
  const [attachDocumentType, setAttachDocumentType] = useState<DocumentType | null>(null)

  return (
    <JelledModal
      onClose={onClose}
      isOpen={true}
      width={isMobile ? 'calc(100% - 32px)' : '960px'}
      modalStyle={{
        backgroundColor: COLORS.PURE_WHITE,
      }}>
      <Stack>
        <Typography mb={4} variant="h4" color="secondary">
          {suggestedQuestion ? 'Edit conversation starter' : 'Add a conversation starter'}
        </Typography>

        <TextField
          className="has-label"
          multiline
          required
          label="Question"
          placeholder="What do you want to be asked about?"
          value={query}
          onChange={onQueryChange}
          disabled={isSubmitting}
          error={submitCount > 0 && isFieldEmpty(query)}
          helperText={submitCount > 0 && isFieldEmpty(query) && 'You must enter a question'}
          sx={{
            mb: 2,
          }}
        />

        <Button
          sx={{
            mt: 1,
            width: 'fit-content',
            color: COLORS.GRAY_2,
          }}
          onClick={onClickToggleGenerateAnswerSection}
          endIcon={
            isGenerateAnswerSectionOpen ? (
              <ArrowDropUpOutlinedIcon
                sx={{
                  height: '16px',
                  width: '16px',
                  color: COLORS.GRAY_2,
                }}
              />
            ) : (
              <ArrowDropDownOutlinedIcon
                sx={{
                  height: '16px',
                  width: '16px',
                  color: COLORS.GRAY_2,
                }}
              />
            )
          }>
          Use AI to generate answers (optional)
        </Button>

        {isGenerateAnswerSectionOpen && (
          <Stack
            mt={2}
            p={2}
            borderRadius="16px"
            sx={{
              backgroundColor: COLORS.GRAY_8,
            }}>
            <TextField
              className="has-label"
              multiline
              label="Instruction"
              placeholder="Tell your twin to..."
              value={instructions}
              onChange={onInstructionsChange}
              disabled={isSubmitting}
              sx={{
                mt: 2,
                mb: 3,
              }}
            />

            <AttachDocumentSelector
              notes={notes}
              setNotes={setNotes}
              sourceDocuments={sourceDocuments}
              setSourceDocuments={setSourceDocuments}
              documentType={attachDocumentType}
              setDocumentType={setAttachDocumentType}
            />

            <AttachedDocumentsList
              notes={notes}
              setNotes={setNotes}
              sourceDocuments={sourceDocuments}
              setSourceDocuments={setSourceDocuments}
            />

            <ReferencesList references={references} setReferences={setReferences} submitCount={submitCount} />

            <LoadingButton
              loadingIndicator={<Typography variant="body0">Generating...</Typography>}
              startIcon={
                <AutoAwesomeIcon
                  sx={{
                    height: '16px',
                    width: '16px',
                    color: COLORS.GRAY_0,
                  }}
                />
              }
              loading={isGeneratingAnswer}
              variant="outlined"
              onClick={onGenerateAnswerClick}
              sx={{
                width: 'fit-content',
                mt: 2,
                color: COLORS.GRAY_0,
                border: `1px solid ${COLORS.GRAY_5}`,
                backgroundColor: COLORS.PURE_WHITE,
                '&:hover': {
                  borderWidth: '1px',
                },
              }}>
              Generate response
            </LoadingButton>
          </Stack>
        )}

        <TextField
          className="has-label"
          multiline
          label="Answer"
          required
          placeholder="Answer"
          value={answer}
          error={submitCount > 0 && isFieldEmpty(answer)}
          helperText={submitCount > 0 && isFieldEmpty(answer) && 'You must provide an answer'}
          onChange={(event) => {
            setAnswer(event.target.value)
          }}
          sx={{
            mt: 6,
            mb: 2,
          }}
        />

        <Stack direction="row" alignItems="center" spacing={1} width="100%">
          <Checkbox
            checked={answerStrictly}
            onChange={(e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
              setAnswerStrictly(checked)
            }}
            color="primary"
            sx={{
              marginRight: '0',
              padding: 0,
            }}
          />
          <Typography variant="pS">Only use the above answer as a context for your response</Typography>
        </Stack>

        <AddDocumentDialog
          isAddDocumentDialogOpen={isAddDocumentDialogOpen}
          setIsAddDocumentDialogOpen={setIsAddDocumentDialogOpen}
          onAddDocument={(sourceDocuments: SourceDocument[]) => {
            setSourceDocuments(sourceDocuments)
          }}
          existingDocuments={sourceDocuments}
        />

        <AddNoteDialog
          isAddNoteDialogOpen={isAddNoteDialogOpen}
          setIsAddNoteDialogOpen={setIsAddNoteDialogOpen}
          onAddNote={(notes: TwinNoteSummary[]) => {
            setNotes(notes)
          }}
          existingNotes={notes}
        />

        {verboseOutput && (
          <Stack>
            <Button
              onClick={() => setIsVerboseLogModalOpen?.(true)}
              style={{
                width: 'fit-content',
                marginTop: '8px',
              }}>
              Open verbose log
            </Button>
            {isVerboseLogModalOpen && (
              <VerboseLogModal
                contextSources={[]}
                onClose={() => setIsVerboseLogModalOpen?.(false)}
                verboseOutput={verboseOutput}
              />
            )}
          </Stack>
        )}

        <Stack direction="row" justifyContent="space-between" alignItems="center" mt={4}>
          <LoadingButton
            loading={isSubmitting}
            size="small"
            variant="outlined"
            color="secondary"
            startIcon={<DeleteOutlined />}
            onClick={onDeleteInstruction}
            sx={{
              visibility: suggestedQuestion ? 'visible' : 'hidden',
            }}>
            Delete
          </LoadingButton>

          <Stack direction="row" alignItems="center" spacing={2}>
            <Button size="medium" color="primary" onClick={() => onClose()}>
              Cancel
            </Button>
            <LoadingButton
              size="medium"
              loading={isSubmitting}
              variant="contained"
              color="primary"
              onClick={suggestedQuestion ? onUpdateInstruction : onAddInstruction}>
              Save
            </LoadingButton>
          </Stack>
        </Stack>
      </Stack>
    </JelledModal>
  )
}

export default SuggestedQuestionModal
